/**
 * AI CONTEXT: api-client - Authorized fetch helper for REST API calls
 * Journal: J-20250812-01
 *
 * Purpose:
 * - Centralize API base URL and Authorization header wiring
 * - Use Cognito ID token from Amplify to authorize against API Gateway
 * - Provide safe fallbacks when API is not configured
 *
 * Environment:
 * - REACT_APP_API_BASE_URL (e.g., https://abc123.execute-api.us-east-2.amazonaws.com/prod)
 */

import { fetchAuthSession } from 'aws-amplify/auth';

// Simple in-flight de-duplication and short-lived response cache to prevent
// bursts of identical requests from hammering the backend
const inflightRequests = new Map(); // key -> Promise
const responseCache = new Map(); // key -> { expiresAt, data }
const CACHEABLE_METHODS = new Set(['GET']);
const DEFAULT_CACHE_TTL_MS = 10000; // 10s safety cache for dashboard data
// GET /products response depends on auth (admin sees admin-only products); do not cache by URL only
const NO_CACHE_PATHS = new Set(['/products', '/admin/products']);
const failureCooldowns = new Map(); // key -> expiresAt for last 503
const DEFAULT_FAILURE_COOLDOWN_MS = 30000; // 30s cooldown after 503

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

function invalidateCachedGetByPathPrefix(pathPrefix) {
  if (!API_BASE_URL || !pathPrefix) return;
  const keyPrefix = `GET ${API_BASE_URL}${pathPrefix}`;
  for (const key of Array.from(responseCache.keys())) {
    if (key.startsWith(keyPrefix)) {
      responseCache.delete(key);
    }
  }
}

export const isApiConfigured = () => Boolean(API_BASE_URL);

/** Base URL for API (e.g. public endpoints that don't use Authorization). */
export const getApiBaseUrl = () => API_BASE_URL;

// Attempt to retrieve a valid ID token with multiple retries to avoid
// race conditions right after sign-in (common on mobile/Safari).
async function getIdTokenWithRetries(maxAttempts = 8) {
  let attempt = 0;
  let lastError;
  while (attempt < maxAttempts) {
    try {
      // First try without forcing refresh
      let session = await fetchAuthSession();
      let idToken = '';
      
      // Handle AWS Amplify v6 token structure properly
      if (session?.tokens?.idToken) {
        if (typeof session.tokens.idToken === 'string') {
          idToken = session.tokens.idToken;
        } else if (session.tokens.idToken.value) {
          idToken = session.tokens.idToken.value;
        } else if (session.tokens.idToken.toString) {
          idToken = session.tokens.idToken.toString();
        }
      }

      if (!idToken) {
        // Force refresh to bypass stale/empty caches
        session = await fetchAuthSession({ forceRefresh: true });
        
        if (session?.tokens?.idToken) {
          if (typeof session.tokens.idToken === 'string') {
            idToken = session.tokens.idToken;
          } else if (session.tokens.idToken.value) {
            idToken = session.tokens.idToken.value;
          } else if (session.tokens.idToken.toString) {
            idToken = session.tokens.idToken.toString();
          }
        }
      }

      if (idToken) {
        if (process.env.NODE_ENV !== 'production') {
          console.log('🔐 API Client: Token retrieved successfully:', { 
            tokenLength: idToken.length, 
            tokenStart: idToken.substring(0, 20) + '...' 
          });
        }
        return idToken;
      }
    } catch (e) {
      lastError = e;
    }

    // Exponential backoff with small jitter
    const backoffMs = Math.min(1000, 100 * Math.pow(1.7, attempt)) + Math.floor(Math.random() * 50);
    await new Promise(r => setTimeout(r, backoffMs));
    attempt += 1;
  }
  if (process.env.NODE_ENV !== 'production' && lastError) {
    console.warn('getIdTokenWithRetries exhausted attempts', lastError);
  }
  return '';
}

export async function authorizedFetch(path, options = {}) {
  if (!API_BASE_URL) {
    throw new Error('API not configured: missing REACT_APP_API_BASE_URL');
  }

  const method = String(options.method || 'GET').toUpperCase();
  const url = `${API_BASE_URL}${path}`;
  const requestKey = `${method} ${url}`;

  // If recent 503 cooldown exists, short-circuit to avoid hammering backend
  const cooldownUntil = failureCooldowns.get(requestKey) || 0;
  if (cooldownUntil > Date.now()) {
    const cached = responseCache.get(requestKey);
    if (cached && cached.expiresAt > Date.now()) return cached.data;
    throw new Error('Service temporarily unavailable (cooldown). Please retry shortly.');
  }

  // Return cached response for cacheable GETs when fresh (skip for paths whose response depends on auth, e.g. /products)
  const pathOnly = path.replace(/\?.*$/, '');
  const useCache = CACHEABLE_METHODS.has(method) && !NO_CACHE_PATHS.has(pathOnly);
  if (useCache) {
    const cached = responseCache.get(requestKey);
    if (cached && cached.expiresAt > Date.now()) {
      return cached.data;
    }
  }

  // De-duplicate in-flight identical requests
  const existing = inflightRequests.get(requestKey);
  if (existing) {
    console.log('🔍 authorizedFetch: Returning existing in-flight request for:', requestKey);
    // Ensure we return the promise result, not the promise function
    try {
      const result = await existing;
      return result;
    } catch (error) {
      // If the existing request failed, remove it and continue with new request
      inflightRequests.delete(requestKey);
      console.log('🔍 authorizedFetch: Existing request failed, proceeding with new request');
    }
  }

  // Get ID token with robust retry logic before making the request
  const idToken = await getIdTokenWithRetries();
  if (!idToken) {
    console.warn('🔍 authorizedFetch: No ID token available - user may not be authenticated');
  } else {
    console.log('🔍 authorizedFetch: ID token obtained, length:', idToken.length);
  }

  const headers = new Headers(options.headers || {});
  // Only set Content-Type when sending a body to avoid unnecessary CORS preflights on GET
  if (options.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (idToken) {
    headers.set('Authorization', `Bearer ${idToken}`);
  }

  console.log('🔍 authorizedFetch: Making request with headers:', Object.fromEntries(headers.entries()));

  const fetchOnce = async () => {
    return fetch(url, { ...options, method, headers });
  };

  const fetchPromise = (async () => {
    let response = await fetchOnce();

    if (!response.ok) {
      const text = await response.text().catch(() => '');
      
      console.log('🔍 authorizedFetch: Response not OK:', {
        status: response.status,
        statusText: response.statusText,
        url,
        body: text.substring(0, 200) // First 200 chars for debugging
      });
      
      // Handle authentication errors specifically
      if (response.status === 401) {
        // Safari-specific detection for enhanced retry logic
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        const isSafariMobile = isSafari && isMobile;
        
        // Handle 401 by attempting token refresh
        if (process.env.NODE_ENV !== 'production') {
          console.log(`🔍 401 received, attempting token refresh${isSafari ? ' (Safari)' : ''}`);
        }

        let retryCount = 0;
        const maxRetries = isSafari ? 3 : 2; // More retries for Safari

        while (retryCount < maxRetries) {
          try {
            // Force refresh the auth session
            const newSession = await fetchAuthSession({ forceRefresh: true });
            let newToken = '';
            
            if (newSession?.tokens?.idToken) {
              if (typeof newSession.tokens.idToken === 'string') {
                newToken = newSession.tokens.idToken;
              } else if (newSession.tokens.idToken.value) {
                newToken = newSession.tokens.idToken.value;
              } else if (newSession.tokens.idToken.toString) {
                newToken = newSession.tokens.idToken.toString();
              }
            }
            
            if (newToken) {
              headers.set('Authorization', `Bearer ${newToken}`);
              response = await fetchOnce();
              
              if (response.ok) {
                if (process.env.NODE_ENV !== 'production') {
                  console.log(`🔍 401 retry successful on attempt ${retryCount + 1}${isSafari ? ' (Safari)' : ''}`);
                }
                // Successful retry, continue normal flow below to parse JSON
                break;
              } else if (response.status === 401) {
                retryCount++;
                if (retryCount >= maxRetries) {
                  console.warn(`🔍 API authentication failed after ${maxRetries} token refresh attempts${isSafari ? ' (Safari)' : ''} - user may need to login again`);
                  throw new Error('Authentication required. Please login again.');
                }
                
                // Safari-specific delay between retries
                if (isSafari) {
                  const delay = isSafariMobile ? 2000 : 1000;
                  if (process.env.NODE_ENV !== 'production') {
                    console.log(`🔍 Safari 401 retry delay: ${delay}ms`);
                  }
                  await new Promise(r => setTimeout(r, delay));
                }
                continue;
              }
            } else {
              retryCount++;
              if (retryCount >= maxRetries) {
                console.warn(`🔍 API auth retry skipped - no token after ${maxRetries} refresh attempts${isSafari ? ' (Safari)' : ''}`);
                throw new Error('Authentication required. Please login again.');
              }
              
              // Safari-specific delay between retries
              if (isSafari) {
                const delay = isSafariMobile ? 2000 : 1000;
                if (process.env.NODE_ENV !== 'production') {
                  console.log(`🔍 Safari token refresh delay: ${delay}ms`);
                }
                await new Promise(r => setTimeout(r, delay));
              }
              continue;
            }
          } catch (retryErr) {
            retryCount++;
            if (retryCount >= maxRetries) {
              if (process.env.NODE_ENV !== 'production') {
                console.warn(`🔍 Auth retry failed after ${maxRetries} attempts${isSafari ? ' (Safari)' : ''}`, retryErr);
              }
              console.warn(`🔍 API authentication failed after ${maxRetries} retry attempts${isSafari ? ' (Safari)' : ''} - user may need to login again`);
              throw new Error('Authentication required. Please login again.');
            }
            
            // Safari-specific delay between retries
            if (isSafari) {
              const delay = isSafariMobile ? 2000 : 1000;
              if (process.env.NODE_ENV !== 'production') {
                console.log(`🔍 Safari retry error delay: ${delay}ms`);
              }
              await new Promise(r => setTimeout(r, delay));
            }
            continue;
          }
        }
      }

      if (process.env.NODE_ENV !== 'production') {
        console.error('API error', { 
          url, 
          status: response.status, 
          statusText: response.statusText, 
          body: text,
          hasToken: !!idToken
        });
      }
      // Apply cooldown on 503 to prevent rapid retries
      if (response.status === 503) {
        failureCooldowns.set(requestKey, Date.now() + DEFAULT_FAILURE_COOLDOWN_MS);
      }
      throw new Error(`API ${response.status} ${response.statusText}: ${text}`);
    }

    if (response.status === 204) return null;

    // Safely parse JSON response with error handling
    let data;
    try {
      data = await response.json();
      console.log('🔍 authorizedFetch: Successful response:', {
        status: response.status,
        url,
        data: data
      });
    } catch (jsonError) {
      const text = await response.text().catch(() => '');
      if (process.env.NODE_ENV !== 'production') {
        console.error('JSON parsing failed:', {
          url,
          status: response.status,
          contentType: response.headers.get('content-type'),
          body: text.substring(0, 500) // First 500 chars for debugging
        });
      }
      throw new Error(`Invalid JSON response from API: ${response.status} ${response.statusText}`);
    }

    // Populate short-lived cache for GETs (skip for paths whose response depends on auth)
    if (useCache) {
      responseCache.set(requestKey, {
        data,
        expiresAt: Date.now() + DEFAULT_CACHE_TTL_MS,
      });
    } else {
      // Mutations affecting giveaways should invalidate cached giveaway reads
      // so admin-created giveaways appear immediately in management views.
      if (method !== 'GET' && pathOnly.startsWith('/giveaways')) {
        invalidateCachedGetByPathPrefix('/giveaways');
      }
    }
    return data;
  })();

  // Track in-flight request and ensure cleanup
  inflightRequests.set(requestKey, fetchPromise);

  try {
    const result = await fetchPromise;
    inflightRequests.delete(requestKey);
    return result;
  } catch (error) {
    inflightRequests.delete(requestKey);
    // Re-throw authentication errors as-is
    if (error.message.includes('Authentication required')) {
      throw error;
    }
    
    // Handle network errors
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      throw new Error('Network error - please check your connection');
    }
    
    throw error;
  }
}

export async function createCheckoutSession({ items, subscription = false, successUrl, cancelUrl, localStorageVerified = false, justVerified = false, taxAmount, mpf }) {
  if (!API_BASE_URL) throw new Error('API not configured');
  const payload = { items, subscription };
  if (successUrl) payload.successUrl = successUrl;
  if (cancelUrl) payload.cancelUrl = cancelUrl;
  // AI CONTEXT: age-verification - Backend expects 'justVerified' parameter to bypass age verification check
  // Pass true if user just completed age verification (localStorage confirmation)
  if (localStorageVerified || justVerified) payload.justVerified = true;
  if (taxAmount !== undefined && taxAmount !== null) payload.taxAmount = taxAmount;
  if (mpf) payload.mpf = mpf;
  
  // Debug logging
  console.log('🔍 createCheckoutSession called with:');
  console.log('  - localStorageVerified:', localStorageVerified);
  console.log('  - justVerified:', justVerified);
  console.log('  - payload.justVerified:', payload.justVerified);
  console.log('  - Full payload:', JSON.stringify(payload, null, 2));
  
  return authorizedFetch('/payments/checkout-session', {
    method: 'POST',
    body: JSON.stringify(payload)
  });
}

export async function getMyPurchases() {
  if (!API_BASE_URL) throw new Error('API not configured');
  return authorizedFetch('/my/purchases', { method: 'GET' });
}

export async function verifyCheckoutSession(sessionId) {
  if (!API_BASE_URL) throw new Error('API not configured');
  const params = new URLSearchParams({ session_id: sessionId });
  return authorizedFetch(`/payments/verify-session?${params.toString()}`, { method: 'GET' });
}

export async function getMyProfile() {
  if (!API_BASE_URL) throw new Error('API not configured');
  return authorizedFetch('/my/profile', { method: 'GET' });
}

export async function updateMyProfile(profileData) {
  if (!API_BASE_URL) throw new Error('API not configured');
  return authorizedFetch('/my/profile', {
    method: 'POST',
    body: JSON.stringify(profileData)
  });
}

export async function getUserProfile(email) {
  if (!API_BASE_URL) throw new Error('API not configured');
  return authorizedFetch(`/admin/user-profile?email=${encodeURIComponent(email)}`, { method: 'GET' });
}

const apiClient = { authorizedFetch, isApiConfigured, createCheckoutSession, getMyPurchases, verifyCheckoutSession, getMyProfile, updateMyProfile, getUserProfile };
export default apiClient;

// Referrals & Coupons

/**
 * Record referral usage (public, no auth). Used at registration so the relationship
 * is tracked and credit is issued. Idempotent on the backend.
 * Includes one automatic retry on network/5xx for robustness.
 * @returns {{ success: boolean, credited?: boolean, message?: string }}
 * @throws on non-ok response after retry or when API is not configured
 */
export async function recordReferral(referralCode, newUserEmail) {
  if (!API_BASE_URL) throw new Error('API not configured');
  const code = String(referralCode || '').trim().toUpperCase();
  const email = String(newUserEmail || '').trim().toLowerCase();
  if (!code || !email) throw new Error('referralCode and newUserEmail required');

  const url = `${API_BASE_URL}/public/record-referral`;
  const body = JSON.stringify({ referralCode: code, newUserEmail: email });
  const opts = { method: 'POST', headers: { 'Content-Type': 'application/json' }, body };

  const attempt = async () => {
    const res = await fetch(url, opts);
    const text = await res.text();
    let data;
    try {
      data = text ? JSON.parse(text) : {};
    } catch {
      data = {};
    }
    if (!res.ok) {
      const err = new Error(data.message || `Record referral failed: ${res.status}`);
      err.status = res.status;
      err.body = data;
      throw err;
    }
    return data;
  };

  try {
    return await attempt();
  } catch (first) {
    const isRetryable = first.status >= 500 || first.status === 408 || first.message?.includes('fetch');
    if (isRetryable) {
      await new Promise((r) => setTimeout(r, 800));
      return await attempt();
    }
    throw first;
  }
}

export async function registerReferral(email) {
  if (!API_BASE_URL) throw new Error('API not configured');
  return authorizedFetch('/public/register-referral', {
    method: 'POST',
    body: JSON.stringify({ email })
  });
}

export async function getMyReferralCode() {
  if (!API_BASE_URL) throw new Error('API not configured');
  return authorizedFetch('/my/referral-code', { method: 'GET' });
}

export async function validateCode(code) {
  if (!API_BASE_URL) throw new Error('API not configured');
  return authorizedFetch('/codes/validate', {
    method: 'POST',
    body: JSON.stringify({ code })
  });
}

export async function getMyReferrals() {
  if (!API_BASE_URL) throw new Error('API not configured');
  return authorizedFetch('/my/referrals', { method: 'GET' });
}

export async function getAdminAllReferrals() {
  if (!API_BASE_URL) throw new Error('API not configured');
  return authorizedFetch('/admin/referrals', { method: 'GET' });
}

export async function backfillReferral(payload) {
  if (!API_BASE_URL) throw new Error('API not configured');
  return authorizedFetch('/admin/referrals/backfill', {
    method: 'POST',
    body: JSON.stringify(payload)
  });
}

// SMS/MMS notification functions
export async function sendWelcomeSMS(phone) {
  if (!API_BASE_URL) throw new Error('API not configured');
  return authorizedFetch('/sms/send-welcome', {
    method: 'POST',
    body: JSON.stringify({ phone })
  });
}

export async function triggerSMSNotifications() {
  if (!API_BASE_URL) throw new Error('API not configured');
  return authorizedFetch('/sms/send-giveaway', {
    method: 'POST',
    body: JSON.stringify({})
  });
}

export async function triggerCombinedNotifications() {
  if (!API_BASE_URL) throw new Error('API not configured');
  return authorizedFetch('/notifications/send-combined', {
    method: 'POST',
    body: JSON.stringify({})
  });
}

// AI Blog module (admin)
export async function getAdminBlogSettings() {
  if (!API_BASE_URL) throw new Error('API not configured');
  return authorizedFetch('/admin/blog/settings', { method: 'GET' });
}

export async function patchAdminBlogSettings(settings, apiKeys = null) {
  if (!API_BASE_URL) throw new Error('API not configured');
  const body = { settings };
  if (apiKeys && typeof apiKeys === 'object') {
    body.apiKeys = apiKeys;
  }
  return authorizedFetch('/admin/blog/settings', {
    method: 'PATCH',
    body: JSON.stringify(body)
  });
}

export async function getAdminBlogPosts() {
  if (!API_BASE_URL) throw new Error('API not configured');
  return authorizedFetch('/admin/blog/posts', { method: 'GET' });
}

export async function getAdminBlogPost(id) {
  if (!API_BASE_URL) throw new Error('API not configured');
  return authorizedFetch(`/admin/blog/posts/${encodeURIComponent(id)}`, { method: 'GET' });
}

export async function patchAdminBlogPost(id, payload) {
  if (!API_BASE_URL) throw new Error('API not configured');
  return authorizedFetch(`/admin/blog/posts/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload)
  });
}

export async function deleteAdminBlogPost(id) {
  if (!API_BASE_URL) throw new Error('API not configured');
  return authorizedFetch(`/admin/blog/posts/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export async function generateBlogArticle() {
  if (!API_BASE_URL) throw new Error('API not configured');
  return authorizedFetch('/admin/blog/generate', { method: 'POST', body: JSON.stringify({}) });
}

export async function syncBlogMemory() {
  if (!API_BASE_URL) throw new Error('API not configured');
  return authorizedFetch('/admin/blog/memory/sync', { method: 'POST', body: JSON.stringify({}) });
}

export async function getAdminBlogJobs() {
  if (!API_BASE_URL) throw new Error('API not configured');
  return authorizedFetch('/admin/blog/jobs', { method: 'GET' });
}

// AI Blog module (public)
export async function getPublicBlogPosts() {
  if (!API_BASE_URL) throw new Error('API not configured');
  const res = await fetch(`${API_BASE_URL}/public/blog/posts`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export async function getPublicBlogPost(slug) {
  if (!API_BASE_URL) throw new Error('API not configured');
  const res = await fetch(`${API_BASE_URL}/public/blog/posts/${encodeURIComponent(slug)}`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export async function submitAcquisitionRequest(payload) {
  if (!API_BASE_URL) throw new Error('API not configured');
  const res = await fetch(`${API_BASE_URL}/public/acquisition-requests`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.message || `HTTP ${res.status}`);
  }
  return data;
}

export async function getAdminAcquisitionRequests() {
  if (!API_BASE_URL) throw new Error('API not configured');
  return authorizedFetch('/admin/acquisition-requests', { method: 'GET' });
}

export async function updateAdminAcquisitionRequest(id, payload) {
  if (!API_BASE_URL) throw new Error('API not configured');
  return authorizedFetch(`/admin/acquisition-requests/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}


